import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Container, Title, Button, Group, Stack, Text, Menu, Badge } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconFileExport, IconFileImport, IconEdit, IconQrcode, IconClipboardPlus, IconChevronDown, IconFileTypeCsv, IconFileTypePdf, IconReportAnalytics } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { supabase } from '@/lib/supabase';
import { usePermissions } from '@/hooks/usePermissions';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';
import { SubscriptionLimitAlert, LimitBlockModal } from '@/components/SubscriptionLimitAlert';
import {
  useAssets,
  useCreateAsset,
  useUpdateAsset,
  useDeleteAsset,
  useAssignAsset,
  useUnassignAsset,
} from '@/hooks/api/useAssets';
import {
  useSavedFilters,
  useCreateSavedFilter,
  useUpdateSavedFilter,
  useDeleteSavedFilter,
  useToggleFavoriteSavedFilter,
} from '@/hooks/api/useSavedFilters';
import { uploadAssetPhoto, deleteAssetPhoto, getPhotoPathFromUrl } from '@/lib/storage';
import { exportAssetsToCSV } from '@/lib/export';
import { exportToCSV, exportToPDF } from '@/lib/reportExport';
import { AssetTable } from './components/AssetTable';
import { AssetForm } from './components/AssetForm';
import { AssetDetailsModal } from './components/AssetDetailsModal';
import { AssignAssetModal } from './components/AssignAssetModal';
import { ImportAssetsModal } from './components/ImportAssetsModal';
import { BulkUpdateModal, type BulkUpdateData } from './components/BulkUpdateModal';
import { AddToAuditModal } from './components/AddToAuditModal';
import { BulkPrintQRModal } from './components/BulkPrintQRModal';
import { FloatingBulkActionBar } from './components/FloatingBulkActionBar';
import { UnassignAssetModal } from './components/UnassignAssetModal';
import { AssetFilterContent, type AssetFilter } from './components/AssetFilterContent';
import { getAssetFilterPresets } from './components/AssetFilterPresets';
import { FilterModal } from '@/components/ui/FilterModal';
import { useAddMultipleAssetsToAudit } from '@/hooks/api/useAudits';
import { usePeople } from '@/hooks/api/usePeople';
import type { Asset } from '@/types/models';
import type { AssetFormValues } from './schemas/assetSchema';
import type { AssetImportRow } from '@/lib/import';

export function AssetsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  const { can } = usePermissions();
  const { data: assets = [], isLoading } = useAssets(organizationId);
  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset();
  const deleteAsset = useDeleteAsset();
  const assignAsset = useAssignAsset();
  const unassignAsset = useUnassignAsset();
  const addToAudit = useAddMultipleAssetsToAudit();
  const { data: people = [] } = usePeople(organizationId);

  // Saved filters
  const { data: savedFilters = [] } = useSavedFilters(organizationId, 'assets');
  const createSavedFilter = useCreateSavedFilter();
  const updateSavedFilter = useUpdateSavedFilter();
  const deleteSavedFilter = useDeleteSavedFilter();
  const toggleFavorite = useToggleFavoriteSavedFilter();

  // Subscription limits
  const {
    canCreateAsset,
    isTrialExpired,
    isAtAssetLimit,
    isOverAssetLimit,
    limitMessage,
  } = useSubscriptionLimits();
  const [limitBlockModalOpened, setLimitBlockModalOpened] = useState(false);

  const [formOpened, setFormOpened] = useState(false);
  const [detailsOpened, setDetailsOpened] = useState(false);
  const [assignOpened, setAssignOpened] = useState(false);
  const [unassignOpened, setUnassignOpened] = useState(false);
  const [assetToUnassign, setAssetToUnassign] = useState<Asset | null>(null);
  const [importOpened, setImportOpened] = useState(false);
  const [bulkUpdateOpened, setBulkUpdateOpened] = useState(false);
  const [addToAuditOpened, setAddToAuditOpened] = useState(false);
  const [bulkPrintQROpened, setBulkPrintQROpened] = useState(false);
  const [filterOpened, setFilterOpened] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isAddingToAudit, setIsAddingToAudit] = useState(false);
  const [filters, setFilters] = useState<AssetFilter>({});

  // Handle opening asset details from URL params (e.g., after QR scan)
  useEffect(() => {
    const viewAssetId = searchParams.get('view');

    if (viewAssetId && assets.length > 0 && !isLoading) {
      const asset = assets.find((a) => a.id === viewAssetId);

      if (asset) {
        setSelectedAsset(asset);
        setDetailsOpened(true);
        // Remove the 'view' param from URL after opening modal
        searchParams.delete('view');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, assets, isLoading, setSearchParams]);

  const handleCreate = () => {
    // Check subscription limits before allowing asset creation
    if (!canCreateAsset) {
      setLimitBlockModalOpened(true);
      return;
    }
    setSelectedAsset(null);
    setIsEditing(false);
    setFormOpened(true);
  };

  const handleEdit = (asset: Asset) => {
    setSelectedAsset(asset);
    setIsEditing(true);
    setDetailsOpened(false); // Close details modal
    setFormOpened(true);
  };

  const handleView = (asset: Asset) => {
    setSelectedAsset(asset);
    setDetailsOpened(true);
  };

  const handleDelete = (asset: Asset) => {
    modals.openConfirmModal({
      title: 'Delete Asset',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{asset.description}</strong>? This action cannot
          be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteAsset.mutate(asset.id);
      },
    });
  };

  const handleAssign = (asset: Asset) => {
    // Warn if asset has a special status
    if (asset.status === 'Maintenance' || asset.status === 'Retired' || asset.status === 'Lost') {
      modals.openConfirmModal({
        title: 'Asset Status Warning',
        children: (
          <Stack gap="sm">
            <Text size="sm">
              This asset is currently marked as <Badge color={
                asset.status === 'Maintenance' ? 'yellow' :
                asset.status === 'Retired' ? 'gray' : 'red'
              }>{asset.status}</Badge>
            </Text>
            <Text size="sm">
              Assigning it will change the status to <Badge color="blue">Assigned</Badge>.
              Are you sure you want to proceed?
            </Text>
          </Stack>
        ),
        labels: { confirm: 'Assign Anyway', cancel: 'Cancel' },
        confirmProps: { color: 'orange' },
        onConfirm: () => {
          setSelectedAsset(asset);
          setAssignOpened(true);
        },
      });
    } else {
      setSelectedAsset(asset);
      setAssignOpened(true);
    }
  };

  const handleUnassign = (asset: Asset) => {
    setAssetToUnassign(asset);
    setUnassignOpened(true);
  };

  const handleUnassignConfirm = async (
    assetId: string,
    newStatus: 'Available' | 'Maintenance' | 'Retired' | 'Lost'
  ) => {
    if (!organizationId) return;
    await unassignAsset.mutateAsync({ assetId, organizationId, newStatus });
    setUnassignOpened(false);
    setAssetToUnassign(null);
  };

  const handleAssignSubmit = async (personId: string) => {
    if (!organizationId || !selectedAsset) return;

    await assignAsset.mutateAsync({
      assetId: selectedAsset.id,
      personId,
      organizationId,
    });

    setAssignOpened(false);
    setSelectedAsset(null);
  };

  const handleSubmit = async (values: AssetFormValues, photo?: File | null, keepOpen = false) => {
    if (!organizationId) return;

    try {
      let photoUrl: string | undefined;

      // Handle photo upload for new assets
      if (photo && !isEditing) {
        // Create a temporary asset ID for upload (will be replaced with real ID after creation)
        const tempAssetId = crypto.randomUUID();
        const uploadResult = await uploadAssetPhoto(organizationId, tempAssetId, photo);

        if (uploadResult) {
          photoUrl = uploadResult.url;
        } else {
          notifications.show({
            title: 'Photo upload failed',
            message: 'The asset will be created without a photo',
            color: 'yellow',
          });
        }
      }

      // Handle photo upload for existing assets
      if (photo && isEditing && selectedAsset) {
        // Delete old photo if it exists
        if (selectedAsset.photo_url) {
          const oldPhotoPath = getPhotoPathFromUrl(selectedAsset.photo_url);
          if (oldPhotoPath) {
            await deleteAssetPhoto(oldPhotoPath);
          }
        }

        // Upload new photo
        const uploadResult = await uploadAssetPhoto(organizationId, selectedAsset.id, photo);

        if (uploadResult) {
          photoUrl = uploadResult.url;
        } else {
          notifications.show({
            title: 'Photo upload failed',
            message: 'The asset will be updated without changing the photo',
            color: 'yellow',
          });
        }
      }

      // Create or update asset
      if (isEditing && selectedAsset) {
        await updateAsset.mutateAsync({
          id: selectedAsset.id,
          data: {
            ...values,
            ...(photoUrl && { photo_url: photoUrl }),
          },
        });
      } else {
        await createAsset.mutateAsync({
          ...values,
          organization_id: organizationId,
          ...(photoUrl && { photo_url: photoUrl }),
        });
      }

      // Only close the modal if keepOpen is false
      if (!keepOpen) {
        setFormOpened(false);
        setSelectedAsset(null);
      }
    } catch (error) {
      console.error('Error submitting asset:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save asset. Please try again.',
        color: 'red',
      });
    }
  };

  // Apply filters to assets
  const filteredAssets = assets.filter((asset) => {
    // Status filter
    if (filters.status && filters.status.length > 0) {
      if (!filters.status.includes(asset.status)) return false;
    }

    // Category filter
    if (filters.categories && filters.categories.length > 0) {
      if (!asset.category_id || !filters.categories.includes(asset.category_id)) return false;
    }

    // Department filter
    if (filters.departments && filters.departments.length > 0) {
      if (!asset.department_id || !filters.departments.includes(asset.department_id)) return false;
    }

    // Location filter
    if (filters.locations && filters.locations.length > 0) {
      if (!asset.location_id || !filters.locations.includes(asset.location_id)) return false;
    }

    // Date range filter
    if (filters.dateRange?.start || filters.dateRange?.end) {
      if (!asset.purchase_date) return false;
      const purchaseDate = new Date(asset.purchase_date);
      if (filters.dateRange.start && purchaseDate < new Date(filters.dateRange.start)) return false;
      if (filters.dateRange.end && purchaseDate > new Date(filters.dateRange.end)) return false;
    }

    // Cost range filter
    if (filters.costRange?.min !== null && filters.costRange?.min !== undefined) {
      if (!asset.cost || asset.cost < filters.costRange.min) return false;
    }
    if (filters.costRange?.max !== null && filters.costRange?.max !== undefined) {
      if (!asset.cost || asset.cost > filters.costRange.max) return false;
    }

    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch =
        asset.description?.toLowerCase().includes(searchLower) ||
        asset.tag_id?.toLowerCase().includes(searchLower) ||
        asset.brand?.toLowerCase().includes(searchLower) ||
        asset.model?.toLowerCase().includes(searchLower) ||
        asset.serial_number?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Depreciable filter
    if (filters.isDepreciable !== undefined) {
      if (asset.is_depreciable !== filters.isDepreciable) return false;
    }

    return true;
  });

  // Count active filters
  const activeFilterCount = Object.keys(filters).reduce((count, key) => {
    const value = filters[key as keyof AssetFilter];
    if (value === undefined || value === null) return count;
    if (Array.isArray(value) && value.length === 0) return count;
    if (typeof value === 'object' && !Array.isArray(value)) {
      const hasValues = Object.values(value).some(v => v !== null && v !== undefined);
      return hasValues ? count + 1 : count;
    }
    return count + 1;
  }, 0);

  const convertFiltersToReportFormat = (assetFilters: AssetFilter) => {
    const reportFilters: any = {};

    if (assetFilters.status && assetFilters.status.length > 0) {
      reportFilters.status = assetFilters.status;
    }
    if (assetFilters.categories && assetFilters.categories.length > 0) {
      reportFilters.category_id = assetFilters.categories;
    }
    if (assetFilters.departments && assetFilters.departments.length > 0) {
      reportFilters.department_id = assetFilters.departments;
    }
    if (assetFilters.locations && assetFilters.locations.length > 0) {
      reportFilters.location_id = assetFilters.locations;
    }
    if (assetFilters.costRange?.min !== null && assetFilters.costRange?.min !== undefined) {
      reportFilters.cost_min = assetFilters.costRange.min;
    }
    if (assetFilters.costRange?.max !== null && assetFilters.costRange?.max !== undefined) {
      reportFilters.cost_max = assetFilters.costRange.max;
    }
    if (assetFilters.dateRange?.start) {
      reportFilters.purchase_date_from = assetFilters.dateRange.start;
    }
    if (assetFilters.dateRange?.end) {
      reportFilters.purchase_date_to = assetFilters.dateRange.end;
    }
    if (assetFilters.isDepreciable === true) {
      reportFilters.is_depreciable = true;
    }

    return { assets: reportFilters };
  };

  const handleSaveAsReport = () => {
    if (activeFilterCount === 0) {
      notifications.show({
        title: 'No Filters Applied',
        message: 'Apply some filters first, then save as a report',
        color: 'yellow',
      });
      return;
    }
    // Navigate to create report page with filters
    navigate('/reports/create', { state: { filters: convertFiltersToReportFormat(filters) } });
  };

  const handleExport = (format: 'csv' | 'pdf') => {
    const assetsToExport = filteredAssets.length > 0 ? filteredAssets : assets;

    if (assetsToExport.length === 0) {
      notifications.show({
        title: 'No assets to export',
        message: 'Add some assets first before exporting',
        color: 'yellow',
      });
      return;
    }

    if (format === 'csv') {
      exportToCSV(assetsToExport);
    } else {
      exportToPDF(assetsToExport);
    }

    notifications.show({
      title: 'Export successful',
      message: `Exported ${assetsToExport.length} asset(s) to ${format.toUpperCase()}`,
      color: 'green',
    });
  };

  const handleSaveFilter = (name: string, description?: string) => {
    createSavedFilter.mutate({
      organization_id: organizationId,
      name,
      description,
      entity_type: 'assets',
      filter_config: filters,
      is_favorite: true,
    });
  };

  const handleLoadFilter = (loadedFilters: AssetFilter) => {
    setFilters(loadedFilters);
  };

  const handleToggleFavorite = (filterId: string) => {
    const filter = savedFilters.find(f => f.id === filterId);
    if (filter) {
      toggleFavorite.mutate({
        id: filterId,
        isFavorite: filter.is_favorite,
        organizationId,
        entityType: 'assets',
      });
    }
  };

  const handleDeleteFilter = (filterId: string) => {
    modals.openConfirmModal({
      title: 'Delete Saved Filter',
      children: (
        <Text size="sm">
          Are you sure you want to delete this saved filter? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteSavedFilter.mutate({
          id: filterId,
          organizationId,
          entityType: 'assets',
        });
      },
    });
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleApplyFilters = () => {
    // Filters are already applied via filteredAssets
    // This function is just for closing the modal
  };

  const handleImport = async (data: AssetImportRow[]) => {
    if (!organizationId) return;

    setIsImporting(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      // Import assets one by one
      for (const row of data) {
        try {
          await createAsset.mutateAsync({
            organization_id: organizationId,
            tag_id: row.tag_id,
            description: row.description,
            brand: row.brand,
            model: row.model,
            serial_number: row.serial_number,
            status: row.status as any,
            purchase_date: row.purchase_date,
            purchased_from: row.purchased_from,
            cost: row.cost ? parseFloat(row.cost.replace(/[$,]/g, '')) : null,
            warranty_period: row.warranty_period ? parseInt(row.warranty_period) : null,
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to import asset ${row.tag_id}:`, error);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        notifications.show({
          title: 'Import successful',
          message: `Successfully imported ${successCount} asset(s)`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Import completed with errors',
          message: `Imported ${successCount} asset(s), failed ${errorCount}`,
          color: 'yellow',
        });
      }

      setImportOpened(false);
    } catch (error) {
      console.error('Import error:', error);
      notifications.show({
        title: 'Import failed',
        message: 'Failed to import assets. Please try again.',
        color: 'red',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleBulkUpdate = async (updates: BulkUpdateData) => {
    setIsBulkUpdating(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      // Extract person_id from updates (not a direct asset field)
      const { person_id, ...assetUpdates } = updates;

      // Update each selected asset
      for (const assetId of selectedAssetIds) {
        try {
          // Update asset fields if there are any
          if (Object.keys(assetUpdates).length > 0) {
            await updateAsset.mutateAsync({
              id: assetId,
              data: assetUpdates,
            });
          }

          // Handle person assignment if person_id is provided
          if (person_id && organizationId) {
            // Create person_asset record (upsert to avoid duplicates)
            const { error: assignError } = await supabase
              .from('person_assets')
              .upsert(
                {
                  organization_id: organizationId,
                  person_id: person_id,
                  asset_id: assetId,
                  assigned_at: new Date().toISOString(),
                },
                { onConflict: 'person_id,asset_id' }
              );

            if (assignError) throw assignError;
          }

          successCount++;
        } catch (error) {
          console.error(`Failed to update asset ${assetId}:`, error);
          errorCount++;
        }
      }

      // Invalidate assets query to refresh assigned_to data
      if (person_id) {
        queryClient.invalidateQueries({ queryKey: ['assets'] });
      }

      if (errorCount === 0) {
        notifications.show({
          title: 'Bulk update successful',
          message: `Successfully updated ${successCount} asset(s)`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Bulk update completed with errors',
          message: `Updated ${successCount} asset(s), failed ${errorCount}`,
          color: 'yellow',
        });
      }

      setSelectedAssetIds([]);
      setBulkUpdateOpened(false);
    } catch (error) {
      console.error('Bulk update error:', error);
      notifications.show({
        title: 'Bulk update failed',
        message: 'Failed to update assets. Please try again.',
        color: 'red',
      });
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleAddToAudit = async (auditId: string) => {
    if (!organizationId) return;

    setIsAddingToAudit(true);
    try {
      const result = await addToAudit.mutateAsync({
        organization_id: organizationId,
        audit_event_id: auditId,
        asset_ids: selectedAssetIds,
      });

      notifications.show({
        title: 'Assets added to audit',
        message: `Successfully added ${result.added.length} asset(s) to the audit${
          result.skipped > 0 ? ` (${result.skipped} already in audit)` : ''
        }`,
        color: 'green',
      });

      setSelectedAssetIds([]);
      setAddToAuditOpened(false);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to add assets to audit. Please try again.',
        color: 'red',
      });
    } finally {
      setIsAddingToAudit(false);
    }
  };

  const handleBulkDelete = () => {
    const selectedAssets = assets.filter((asset) => selectedAssetIds.includes(asset.id));
    const assetList = selectedAssets
      .slice(0, 5)
      .map((a) => a.tag_id || a.description)
      .join(', ');
    const extraCount = selectedAssets.length - 5;

    modals.openConfirmModal({
      title: 'Delete Assets',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            Are you sure you want to delete <strong>{selectedAssets.length}</strong> asset
            {selectedAssets.length > 1 ? 's' : ''}?
          </Text>
          <Text size="sm" c="dimmed">
            {assetList}
            {extraCount > 0 && ` and ${extraCount} more...`}
          </Text>
          <Text size="sm" fw={500} c="red">
            This action cannot be undone.
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Delete All', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        let successCount = 0;
        let errorCount = 0;

        for (const assetId of selectedAssetIds) {
          try {
            await deleteAsset.mutateAsync(assetId);
            successCount++;
          } catch (error) {
            console.error(`Failed to delete asset ${assetId}:`, error);
            errorCount++;
          }
        }

        if (errorCount === 0) {
          notifications.show({
            title: 'Bulk delete successful',
            message: `Successfully deleted ${successCount} asset(s)`,
            color: 'green',
          });
        } else {
          notifications.show({
            title: 'Bulk delete completed with errors',
            message: `Deleted ${successCount} asset(s), failed ${errorCount}`,
            color: 'yellow',
          });
        }

        setSelectedAssetIds([]);
      },
    });
  };

  return (
    <Container fluid>
      <FloatingBulkActionBar
        selectedCount={selectedAssetIds.length}
        onPrintQR={() => setBulkPrintQROpened(true)}
        onAddToAudit={() => setAddToAuditOpened(true)}
        onBulkUpdate={() => setBulkUpdateOpened(true)}
        onBulkDelete={handleBulkDelete}
        onClearSelection={() => setSelectedAssetIds([])}
      />

      <Stack gap="lg">
        {/* Show subscription limit alert when at/over limit or trial expiring */}
        <SubscriptionLimitAlert
          show={isTrialExpired || isAtAssetLimit || isOverAssetLimit}
        />

        <Group justify="apart">
          <div>
            <Title order={1}>Assets</Title>
            <Text c="dimmed" size="sm">
              Manage your organization's assets
            </Text>
          </div>
          <Group>
            <Button
              variant="light"
              leftSection={<IconQrcode size={16} />}
              onClick={() => navigate('/assets/scan')}
            >
              Scan QR
            </Button>
            {can('assets', 'write') && (
              <Button
                variant="light"
                leftSection={<IconFileImport size={16} />}
                onClick={() => setImportOpened(true)}
              >
                Import
              </Button>
            )}
            {can('assets', 'view') && (
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button
                    variant="light"
                    leftSection={<IconFileExport size={16} />}
                    rightSection={<IconChevronDown size={14} />}
                    disabled={filteredAssets.length === 0}
                  >
                    Export
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconFileTypeCsv size={16} />}
                    onClick={() => handleExport('csv')}
                  >
                    Export as CSV
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconFileTypePdf size={16} />}
                    onClick={() => handleExport('pdf')}
                  >
                    Export as PDF
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
            {can('assets', 'write') && (
              <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
                Add Asset
              </Button>
            )}
          </Group>
        </Group>

        {activeFilterCount > 0 && (
          <Group justify="space-between" align="center">
            <Text size="sm" c="dimmed">
              Showing {filteredAssets.length} of {assets.length} assets ({activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} applied)
            </Text>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconReportAnalytics size={14} />}
              onClick={handleSaveAsReport}
            >
              Create Report from This View
            </Button>
          </Group>
        )}

        <AssetTable
          assets={filteredAssets}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAssign={handleAssign}
          onUnassign={handleUnassign}
          isLoading={isLoading}
          selectedIds={selectedAssetIds}
          onSelectionChange={setSelectedAssetIds}
          onOpenFilters={() => setFilterOpened(true)}
          activeFilterCount={activeFilterCount}
        />

        <AssetForm
          opened={formOpened}
          onClose={() => {
            setFormOpened(false);
            setSelectedAsset(null);
          }}
          onSubmit={handleSubmit}
          asset={isEditing ? selectedAsset : null}
          isLoading={createAsset.isPending || updateAsset.isPending}
        />

        <AssetDetailsModal
          opened={detailsOpened}
          onClose={() => {
            setDetailsOpened(false);
            setSelectedAsset(null);
          }}
          asset={selectedAsset}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={async () => {
            // Refetch assets and update selectedAsset with fresh data
            await queryClient.refetchQueries({ queryKey: ['assets', organizationId] });
            if (selectedAsset) {
              const freshAssets = queryClient.getQueryData<Asset[]>(['assets', organizationId]);
              const updatedAsset = freshAssets?.find((a) => a.id === selectedAsset.id);
              if (updatedAsset) {
                setSelectedAsset(updatedAsset);
              }
            }
          }}
        />

        <AssignAssetModal
          opened={assignOpened}
          onClose={() => {
            setAssignOpened(false);
            setSelectedAsset(null);
          }}
          onSubmit={handleAssignSubmit}
          asset={selectedAsset}
          isLoading={assignAsset.isPending}
        />

        <UnassignAssetModal
          asset={assetToUnassign}
          opened={unassignOpened}
          onClose={() => {
            setUnassignOpened(false);
            setAssetToUnassign(null);
          }}
          onConfirm={handleUnassignConfirm}
          loading={unassignAsset.isPending}
        />

        <ImportAssetsModal
          opened={importOpened}
          onClose={() => setImportOpened(false)}
          onImport={handleImport}
          isLoading={isImporting}
        />

        <BulkUpdateModal
          opened={bulkUpdateOpened}
          onClose={() => setBulkUpdateOpened(false)}
          onUpdate={handleBulkUpdate}
          selectedCount={selectedAssetIds.length}
          isLoading={isBulkUpdating}
          people={people}
        />

        <AddToAuditModal
          opened={addToAuditOpened}
          onClose={() => setAddToAuditOpened(false)}
          onSubmit={handleAddToAudit}
          selectedCount={selectedAssetIds.length}
          isLoading={isAddingToAudit}
        />

        <BulkPrintQRModal
          opened={bulkPrintQROpened}
          onClose={() => setBulkPrintQROpened(false)}
          assets={assets.filter((asset) => selectedAssetIds.includes(asset.id))}
        />
        <FilterModal
          opened={filterOpened}
          onClose={() => setFilterOpened(false)}
          title="Filter Assets"
          filters={filters}
          onFiltersChange={setFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onSave={handleSaveFilter}
          onLoadSaved={handleLoadFilter}
          onToggleFavorite={handleToggleFavorite}
          onDeleteSaved={handleDeleteFilter}
          presets={getAssetFilterPresets()}
          savedFilters={savedFilters.map(f => ({
            id: f.id,
            name: f.name,
            description: f.description,
            filters: f.filter_config,
            isFavorite: f.is_favorite,
            createdAt: f.created_at,
          }))}
          activeFilterCount={activeFilterCount}
        >
          <AssetFilterContent filters={filters} onFiltersChange={setFilters} />
        </FilterModal>

        {/* Subscription limit block modal */}
        <LimitBlockModal
          opened={limitBlockModalOpened}
          onClose={() => setLimitBlockModalOpened(false)}
          action="add more assets"
        />
      </Stack>
    </Container>
  );
}
